import useRequest from "@/lib/request/useRequest";
import { Field, NStatus } from "@/types/field";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    ColumnFiltersState,
} from "@tanstack/react-table";
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

export const Route = createFileRoute("/dashboard/home/dashboard")({
    component: RouteComponent,
});
import {
    AutocompleteItem,
    Button,
    Checkbox,
    CheckboxGroup,
    Chip,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    useDisclosure,
    Selection,
} from "@heroui/react";
import { roundTo } from "@/lib/utils/numbers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpFromBracket,
    faDownload,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import NStatusChip from "@/components/common/NStatusChip";
import useAuth from "@/lib/auth/useAuth";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { User } from "@/types/user";
import useIsMobile from "@/lib/utils/useIsMobile";
import CommonTable from "@/components/table/CommonTable";
import debounce from "lodash.debounce";
import { getColumnValues } from "@/lib/utils/table";
import PreviewDrawer from "@/components/home/PreviewDrawer";
import useSessionPreferences from "@/lib/sessionPreferences/useSessionPreferences";
import dayjs from "dayjs";
import RecommendationChip from "@/components/common/RecommendationChip";

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { userIsSentinel } = useAuth();
    const { isMobile } = useIsMobile();
    const { t } = useTranslation("home-dashboard");
    const { formatDateMMDDYYYY } = useI18n();
    const fieldsQuery = useGetRequest<Field[]>({
        url: "fields/",
    });

    const tableData = useMemo(() => {
        return fieldsQuery.data ?? [];
    }, [fieldsQuery.data]);

    const columns: ColumnDef<Field>[] = useMemo(() => {
        const tableColumns: ColumnDef<Field>[] = [
            {
                header: t("farm"),
                id: "farm",
                accessorKey: "grower",
            },
            {
                header: t("field"),
                id: "field",
                accessorKey: "name",
                enableColumnFilter: false,
            },
            {
                header: t("latest_rec_date"),
                id: "latest_image",
                accessorFn: (row) => {
                    return (
                        row.recommendation?.user_recommendation
                            ?.recommendation_date ??
                        row.recommendation?.recommendation_date ??
                        undefined
                    );
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return null;
                    }
                    return (
                        <div>
                            {formatDateMMDDYYYY(info.getValue() as string)}
                        </div>
                    );
                },
                size: 70,
                sortUndefined: "last",
                enableColumnFilter: false,
            },
            {
                header: t("sufficiency"),
                id: "sufficiency",
                accessorFn: (row) => {
                    return row.recommendation?.si_mean ?? undefined;
                },
                size: 60,
                sortingFn: "datetime",
                cell: (info) => {
                    if (!info.getValue()) {
                        return null;
                    }
                    return (
                        <div>
                            {roundTo((info.getValue() as number) * 100, 1)} %
                        </div>
                    );
                },
                sortUndefined: "last",
                enableColumnFilter: false,
            },
            {
                header: t("n_status"),
                id: "n_status",
                accessorFn: (row) => {
                    return row.recommendation?.n_status ?? undefined;
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return "--";
                    }
                    return <NStatusChip nStatus={info.getValue() as NStatus} />;
                },
                sortUndefined: "last",
            },
            {
                header: t("recommendation"),
                id: "recommendation",
                accessorFn: (row) => {
                    if (!row.recommendation) {
                        return undefined;
                    }
                    if (
                        row.recommendation.user_recommendation != null &&
                        row.recommendation.user_recommendation
                            .recommend_application != null
                    ) {
                        return row.recommendation.user_recommendation
                            .recommend_application
                            ? t("apply_n")
                            : t("do_not_apply_n");
                    }
                    if (row.recommendation.gap) {
                        return t("gap");
                    }
                    if (
                        row.recommendation?.recommend_fertigation ===
                        undefined ||
                        row.recommendation?.recommend_fertigation === null
                    ) {
                        return undefined;
                    }
                    return row.recommendation?.recommend_fertigation
                        ? t("apply_n")
                        : t("do_not_apply_n");
                },
                cell: (info) => {
                    const rec = info.getValue() as string;
                    if (rec === undefined || rec === "") {
                        return "--";
                    }
                    const notes =
                        info.row.original.recommendation.user_recommendation
                            ?.notes;
                    const user =
                        info.row.original.recommendation.user_recommendation
                            ?.user;
                    return (
                        <RecommendationChip
                            recommendation={info.row.original.recommendation}
                            notes={notes ?? undefined}
                            user={user ?? undefined}
                        />
                    );
                },
                sortUndefined: "last",
            },
        ];

        if (isMobile) {
            tableColumns.splice(4, 1);
            tableColumns.splice(2, 1);
        }

        if (userIsSentinel) {
            tableColumns.splice(0, 0, {
                header: t("csp"),
                id: "csp",
                accessorFn: (row: Field) => {
                    return row.service_provider?.name ?? "N/A";
                },
                sortUndefined: "last",
            });
            tableColumns.splice(0, 0, {
                header: t("account_manager"),
                id: "account_manager",
                accessorFn: (row: Field) => {
                    return (
                        row.service_provider?.account_manager?.full_name ??
                        "N/A"
                    );
                },
            });
        }

        return tableColumns;
    }, [t, isMobile, userIsSentinel, formatDateMMDDYYYY]);

    const { homeFilters, setHomeFilters } = useSessionPreferences();
    const [globalSearch, setGlobalSearch] = useState("");
    const [globalFilter, setGlobalFilter] = useState(
        homeFilters.globalFilter ?? "",
    );
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        (homeFilters.columnFilters as ColumnFiltersState) ?? [],
    );

    useEffect(() => {
        setHomeFilters({
            globalFilter: globalFilter,
            columnFilters: columnFilters as {
                id: string;
                value: string | null;
            }[],
        });
    }, [columnFilters, globalFilter, setHomeFilters]);

    useEffect(() => {
        const debouncedUpdate = debounce(() => {
            setGlobalFilter(globalSearch);
        }, 200);

        debouncedUpdate();

        return () => debouncedUpdate.cancel();
    }, [globalSearch]);

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
            columnFilters,
        },
        initialState: {
            sorting: [
                {
                    id: "field",
                    desc: true,
                },
            ],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
        onColumnFiltersChange: setColumnFilters,
    });

    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(550);

    useEffect(() => {
        if (ref.current) {
            setHeight(ref.current.clientHeight);
        }
    }, [fieldsQuery.status]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (ref.current) {
                setHeight(ref.current.clientHeight);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const { rows } = table.getRowModel();
    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = useMemo(() => {
        const filters = {
            farm: getColumnValues("farm", prefilteredRows).map((farm) => {
                return (
                    <AutocompleteItem
                        key={farm}
                        textValue={farm ?? t("no_farm")}
                    >
                        {farm}
                    </AutocompleteItem>
                );
            }),
            n_status: [
                t("deficient"),
                t("approaching_deficiency"),
                t("sufficient"),
            ].map((status) => {
                const nStatus =
                    status === t("deficient")
                        ? "red"
                        : status === t("approaching_deficiency")
                            ? "yellow"
                            : "green";
                return (
                    <AutocompleteItem
                        key={nStatus}
                        textValue={status ?? t("no_status")}
                    >
                        <NStatusChip nStatus={nStatus} />
                    </AutocompleteItem>
                );
            }),
            recommendation: [t("apply_n"), t("do_not_apply_n"), t("gap")].map(
                (rec) => {
                    return (
                        <AutocompleteItem
                            key={rec}
                            textValue={rec ?? t("no_rec")}
                        >
                            <Chip
                                radius="sm"
                                size="sm"
                                classNames={{
                                    base: classNames({
                                        "text-white": true,
                                        "bg-red-100": rec == t("apply_n"),
                                        "bg-green-100":
                                            rec == t("do_not_apply_n"),
                                        "bg-gray-100": rec == t("gap"),
                                    }),
                                    content: classNames({
                                        "font-bold": true,
                                        "text-red-700": rec == t("apply_n"),
                                        "text-green-700":
                                            rec == t("do_not_apply_n"),
                                        "text-gray-700": rec == t("gap"),
                                    }),
                                }}
                            >
                                {rec}
                            </Chip>
                        </AutocompleteItem>
                    );
                },
            ),
        };
        if (userIsSentinel) {
            return {
                ...filters,
                csp: getColumnValues("csp", prefilteredRows)
                    .filter((csp) => !!csp)
                    .map((csp) => {
                        return (
                            <AutocompleteItem
                                key={csp}
                                textValue={csp ?? "N/A"}
                            >
                                {csp}
                            </AutocompleteItem>
                        );
                    }),
                account_manager: getColumnValues(
                    "account_manager",
                    prefilteredRows,
                )
                    .filter((account_manager) => !!account_manager)
                    .map((account_manager) => {
                        return (
                            <AutocompleteItem
                                key={account_manager}
                                textValue={account_manager ?? "N/A"}
                            >
                                {account_manager}
                            </AutocompleteItem>
                        );
                    }),
            };
        }
        return filters;
    }, [userIsSentinel, prefilteredRows, t]);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const usersQuery = useGetRequest<User[]>({
        url: "users/",
    });

    const [shareSearch, setShareSearch] = useState("");

    const userOptions = useMemo(() => {
        return sortArrayByKey(
            Object.values(
                usersQuery?.data
                    ?.filter((row) => {
                        if (!shareSearch) {
                            return true;
                        }
                        const search = shareSearch.toLowerCase();
                        return (
                            row.full_name?.toLowerCase().includes(search) ||
                            (row.organization as string)
                                ?.toLowerCase()
                                .includes(search)
                        );
                    })
                    .reduce(
                        (acc, user) => {
                            if (!acc[user.organization as string]) {
                                acc[user.organization as string] = {
                                    name: user.organization as string,
                                    users: [],
                                };
                            }
                            acc[user.organization as string].users.push(user);
                            return acc;
                        },
                        {} as Record<string, { name: string; users: User[] }>,
                    ) ?? {},
            ) ?? [],
            "name",
        );
    }, [shareSearch, usersQuery.data]);

    const shareFields = useMutateRequest({
        successMessage: t("status_report_sent"),
        errorMessage: t("failed_to_send_status_report"),
    });

    const fieldIds = table
        .getFilteredRowModel()
        .rows.map((row) => row.original.id);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const onShare = useCallback(() => {
        shareFields.mutate(
            {
                method: "post",
                url: "users/status_report/",
                data: {
                    user_ids: selectedUsers,
                    field_ids: fieldIds,
                },
            },
            {
                onSuccess: () => {
                    onClose();
                },
            },
        );
    }, [fieldIds, onClose, selectedUsers, shareFields]);

    const downloadPDF = useCallback(() => {
        const columns = [
            t("farm"),
            t("field"),
            t("sufficiency"),
            t("n_status"),
            t("recommendation"),
        ];
        const tableRows = rows.map((row) => [
            row.getValue("farm"),
            row.getValue("field"),
            row.getValue("sufficiency")
                ? `${roundTo((row.getValue("sufficiency") as number) * 100, 1)} %`
                : "",
            row.getValue("n_status") === "green"
                ? t("sufficient")
                : row.getValue("n_status") === "yellow"
                    ? t("approaching_deficiency")
                    : row.getValue("n_status") === "red"
                        ? t("deficient")
                        : null,
            row.getValue("recommendation"),
        ]);

        const doc = new jsPDF();
        const image = new Image();
        image.src = "/assets/images/H-NTime-R.png";
        image.onload = () => {
            doc.addImage(image, "PNG", 15, 10, 50, 20);
            const today = formatDateMMDDYYYY(dayjs());
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.setFontSize(12);
            doc.text(`${today}`, pageWidth - 40, 20);
            autoTable(doc, {
                columns: columns,
                body: tableRows as RowInput[],
                startY: 40,
                theme: "grid",
                headStyles: {
                    fillColor: [0, 51, 102],
                    textColor: [255, 255, 255],
                },
            });
            doc.save("N-Time Report.pdf");
        };
    }, [rows, t, formatDateMMDDYYYY]);

    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));

    const {
        isOpen: drawerOpen,
        onOpen: onDrawerOpen,
        onClose: onDrawerClose,
    } = useDisclosure();

    const selectedField = useMemo(() => {
        return (
            table.getFilteredRowModel().rows.find((row) => {
                if (selectedKeys === "all") {
                    return false;
                }

                return [...selectedKeys][0] == row.original.id.toString();
            })?.original ?? undefined
        );
    }, [selectedKeys, table]);

    return (
        <div className="flex flex-col gap-2 h-full overflow-hidden">
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    scrollBehavior="inside"
                >
                    <ModalContent>
                        <ModalHeader>{t("share")}</ModalHeader>
                        <ModalBody>
                            <Input
                                aria-label={t("search")}
                                placeholder={t("search_placeholder")}
                                value={shareSearch}
                                onChange={(e) =>
                                    setShareSearch(String(e.target.value))
                                }
                            />
                            <CheckboxGroup
                                value={selectedUsers}
                                onValueChange={setSelectedUsers}
                                aria-label={t("select_users")}
                            >
                                {userOptions.map((org) => {
                                    return (
                                        <div
                                            key={org.name}
                                            className="flex flex-col "
                                        >
                                            <div className="font-semibold">
                                                {org.name}
                                            </div>
                                            {org.users.map((user) => {
                                                return (
                                                    <Checkbox
                                                        key={user.id}
                                                        value={user.id.toString()}
                                                    >
                                                        {user.full_name}
                                                    </Checkbox>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </CheckboxGroup>
                        </ModalBody>
                        <ModalFooter className="flex flex-col gap-2">
                            {t("fields_shared_description")}
                            <div className="flex flex-row gap-2 items-center justify-end">
                                <Button variant="faded" onPress={onClose}>
                                    {t("cancel")}
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={onShare}
                                    isDisabled={selectedUsers.length === 0}
                                    isLoading={shareFields.isPending}
                                >
                                    {t("share")}
                                </Button>
                            </div>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
            <div className="flex flex-col-reverse md:flex-row gap-2 md:items-center items-start justify-between p-1">
                <div>
                    <Input
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        value={globalSearch}
                        isClearable
                        onClear={() => setGlobalSearch("")}
                        onChange={(e) =>
                            setGlobalSearch(String(e.target.value))
                        }
                        placeholder={t("search_placeholder")}
                    />
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        color="primary"
                        onPress={onOpen}
                        startContent={
                            <FontAwesomeIcon icon={faArrowUpFromBracket} />
                        }
                    >
                        {t("share")}
                    </Button>
                    <Button
                        color="primary"
                        onPress={downloadPDF}
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                    >
                        {t("download_pdf")}
                    </Button>
                </div>
            </div>
            <div
                className="w-full p-1 h-full overflow-y-scroll scrollbar-hide"
                ref={ref}
            >
                <CommonTable
                    isLoading={fieldsQuery.isLoading}
                    label={t("fields_table")}
                    table={table}
                    height={height}
                    filterOptions={filterOptions}
                    tableProps={{
                        selectedKeys,
                        onSelectionChange: (e) => {
                            setSelectedKeys(e);
                            onDrawerOpen();
                        },
                        color: "primary",
                        selectionMode: "single",
                        scrollRef: ref,
                    }}
                />
            </div>
            {selectedField && (
                <PreviewDrawer
                    drawerOpen={drawerOpen}
                    onDrawerOpen={onDrawerOpen}
                    onDrawerClose={onDrawerClose}
                    setSelectedKeys={setSelectedKeys}
                    selectedField={selectedField}
                />
            )}
        </div>
    );
}
